import {get} from 'object-path-immutable'
import React, {useCallback, useEffect, useMemo} from 'react'
import InformationSmall from '../assets/icons/information-small.svg'
import WarningSmall from '../assets/icons/warning_small.svg'
import {Variable} from '@atomic.io/common/types/variable-engine'
import {
  supportsRequestOverride,
  validNameRegex,
  validProfileVarName
} from '@atomic.io/common/variable-engine/index'
import {FormSpacer} from '../components/form'
import {Button, IconButton} from '../components/library/buttons'
import {SelectCreatable} from '../components/library/dropdown/filterable-dropdown'
import {Select, TextInput} from '../components/library/form'
import styled from 'styled-components'
import {Loader} from '../components/loader'
import {omit} from '../lib/updaters'
import {ActionFlowOption, useSfmcActionFlows} from './action-flows'
import {ActivityState} from './journey-builder'

type ConfigurationFormProps = ActivityState & {
  organisationId: string
  environmentId: string
}

export const ConfigurationForm = ({
  organisationId,
  environmentId,
  data,
  setTargetUser,
  setVariables,
  setMetadata,
  setUrl,
  submissionAttempted,
  setFormValid
}: ConfigurationFormProps) => {
  const {actionFlows, loading} = useSfmcActionFlows({
    organisationId,
    environmentId
  })

  const mappings = useMemo(
    () => data.state?.variables ?? {},
    [data.state?.variables]
  )

  const actionFlowId = data.metaData.actionFlowId
    ? data.metaData.actionFlowId.toString()
    : undefined

  const actionFlow = useMemo(
    () => actionFlows.find(af => af.id === actionFlowId),
    [actionFlowId, actionFlows]
  )

  /** variables available on the action flow */
  const actionFlowVariables = useMemo(
    () => getVariables(actionFlow),
    [actionFlow]
  )
  const unusedFlowVariables = useMemo(
    () => Object.keys(actionFlowVariables).filter(v => !(v in mappings)),
    [actionFlowVariables, mappings]
  )
  const unavailableVariables = useMemo(() => Object.keys(mappings), [mappings])
  const pickActionFlow = useCallback(
    (flowId: string) => {
      const newAF = actionFlows.find(af => af.id === flowId)
      const newAfVars = Object.keys(getVariables(newAF))
      // if variables weren't populated before
      const toRemove = Object.entries(mappings)
        .filter(
          ([varName, mapped]) =>
            !mapped &&
            Object.keys(actionFlowVariables).includes(varName) &&
            !newAfVars.includes(varName)
        )
        .map(([varName]) => varName)
      setMetadata(md => ({...md, actionFlowId: flowId ?? ''}))
      setVariables(vars => {
        const updated = omit(
          {
            ...Object.fromEntries(newAfVars.map(name => [name, ''])), // set an empty value by default
            ...vars // if we already mapped a value, keep the mapping
          },
          toRemove
        )
        return updated
      })
      setUrl(newAF?.sfConnectorUrl ?? '')
    },
    [
      actionFlows,
      mappings,
      setUrl,
      setVariables,
      setMetadata,
      actionFlowVariables
    ]
  )

  const targetUserIdValid = useMemo(
    () =>
      !!data.state?.targetUserIds?.length &&
      !!data.state?.targetUserIds?.every(id => !!id),
    [data.state?.targetUserIds]
  )

  // there is a flow id but no action flow
  const flowMissing = !!actionFlowId && !loading && !actionFlow

  // if a required field is missed we need to let the user know
  const missingRequiredVariables: undefined | string[] = useMemo(() => {
    const missing = Object.entries(actionFlowVariables)
      .filter(
        ([name, varDef]) =>
          !!get(varDef, 'eventOverride.required') && !(name in mappings)
      )
      .map(([name]) => name)
    return missing.length ? missing : undefined
  }, [actionFlowVariables, mappings])

  const allFieldsValid = useMemo(
    () => {
      const mappedCorrectly = Object.entries(mappings).every(([key, value]) => {
        const atomicVariable = actionFlowVariables[key]
        const error =
          getMappingValidation(value, atomicVariable)?.variant === 'warning'
        return !error
      })

      return mappedCorrectly && !missingRequiredVariables
    },
    // Note that we disable the removal of required variables,
    // so we don't need to check for their presence here
    [mappings, actionFlowVariables, missingRequiredVariables]
  )
  // update state with new validity
  useEffect(() => {
    setFormValid(allFieldsValid && targetUserIdValid && !flowMissing)
  }, [allFieldsValid, targetUserIdValid, setFormValid, flowMissing])

  if (loading) {
    return <Loader />
  }

  return (
    <ConfigWrap>
      <Intro />

      <InlinePair>
        <div style={{gridArea: 'flow-id'}}>
          <Select
            label="Select Action Flow"
            options={actionFlows
              .filter(af => !!af.sfConnectorUrl)
              .map(af => ({
                value: af.id,
                text: `${af.name}${
                  af.configStatus !== 'PUBLISHED' ? ` (${af.configStatus})` : ''
                }`
              }))}
            onChange={pickActionFlow}
            value={
              data.metaData.actionFlowId
                ? data.metaData.actionFlowId.toString()
                : undefined
            }
          />
        </div>
        {flowMissing && (
          <div style={{gridArea: 'flow-missing'}}>
            <Info
              variant={'warning'}
              message={`Action Flow ( ${actionFlowId} ) can't be loaded. It may have been archived.`}
            />
          </div>
        )}
        <TextInput
          style={{gridArea: 'user-id'}}
          label="Target user id"
          value={String(data.state?.targetUserIds?.[0] ?? '')}
          onChange={targetUserId => setTargetUser(targetUserId)}
          error={submissionAttempted && !targetUserIdValid}
        />
        <div style={{gridArea: 'info'}}>
          <Info
            variant={
              submissionAttempted && !targetUserIdValid ? 'warning' : 'info'
            }
            message={'Required e.g. {{Contact.Key}}'}
          />
        </div>
      </InlinePair>
      <FormSpacer />
      {actionFlow && (
        <VariableMappingContainer>
          <h4>Variables</h4>

          {Object.entries(mappings).map(([key, value]) => (
            <VariableOption
              key={key}
              varKey={key}
              value={value}
              updateKey={newKey => {
                setVariables(vars => {
                  const updated = {...vars}
                  delete updated[key]
                  updated[newKey] = value
                  return updated
                })
              }}
              updateValue={newValue => {
                setVariables(c => ({...c, [key]: newValue}))
              }}
              deleteProperty={() => {
                setVariables(vars => {
                  const updated = {...vars}
                  delete updated[key]
                  return updated
                })
              }}
              unavailableVariables={unavailableVariables}
              unusedFlowVariables={unusedFlowVariables}
              atomicVariable={actionFlowVariables[key]}
              submissionAttempted={submissionAttempted}
            />
          ))}
          <Inline>
            <Button
              size="small"
              appearance="secondary"
              // add an empty entry state
              onClick={() => setVariables(vars => ({...vars, '': ''}))}
              // disable if there's already a blank entry
              disabled={'' in mappings}
            >
              Map another variable
            </Button>
            {missingRequiredVariables && (
              <Info
                variant="warning"
                message={`Required variable${
                  missingRequiredVariables.length > 1 ? 's' : ''
                } need${
                  missingRequiredVariables.length > 1 ? '' : 's'
                } mapping: ${missingRequiredVariables.join(', ')}`}
              />
            )}
          </Inline>
        </VariableMappingContainer>
      )}
    </ConfigWrap>
  )
}

const VariableMappingContainer = styled.div`
  padding: 0 20px 20px 20px;
  border-radius: 5px;
  border: 1px solid ${({theme}) => theme.colors.grey8};
`

const StyledInputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr max-content;
  grid-template-rows: 1fr min-content;
  gap: 0 4px;
  grid-template-areas: 'key value remove' '. description .';
  margin-bottom: 20px;
`

const FieldInfo = styled.div`
  grid-area: description;
  margin-top: 8px;
`

const Remove = styled.div`
  grid-area: remove;
`

const Inline = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const InlinePair = styled.div`
  gap: 8px 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr min-content;
  grid-template-areas: 'flow-id user-id' 'flow-missing info';
`

type VariableOptionProps = {
  varKey?: string
  updateKey: (v: string) => void
  unusedFlowVariables: string[]
  unavailableVariables: string[]
  value?: string
  updateValue: (v: string) => void
  deleteProperty: () => void
  atomicVariable?: Variable
  submissionAttempted: boolean
}

const VariableOption = ({
  varKey,
  updateKey,
  unusedFlowVariables,
  unavailableVariables,
  value,
  updateValue,
  deleteProperty,
  atomicVariable,
  submissionAttempted
}: VariableOptionProps) => {
  // Get the format message unless variable key hasn't been specified yet
  const formatMessage = varKey
    ? getMappingValidation(value, atomicVariable)
    : undefined

  const isDisabled =
    atomicVariable && !!get(atomicVariable, 'eventOverride.required')

  return (
    <StyledInputGroup>
      <SelectCreatable
        options={unusedFlowVariables.map(v => ({
          value: v,
          label: v
        }))}
        value={varKey ? {value: varKey, label: varKey} : null}
        onChange={(v: any) => updateKey(v.value as string)}
        formatCreateLabel={label => <Centred>Add "{label}"</Centred>}
        isDisabled={!!isDisabled || !!varKey}
        isValidNewOption={suggestedName =>
          !!suggestedName &&
          !unavailableVariables.includes(suggestedName) &&
          (!!suggestedName.match(validNameRegex) ||
            !!suggestedName.match(validProfileVarName))
        }
        noOptionsMessage={({inputValue}) => {
          if (!inputValue) return null
          return (
            <Centred>
              {unavailableVariables.includes(inputValue)
                ? `"${inputValue}" already in use`
                : 'Variables must be alphanumeric and may include underscores'}
            </Centred>
          )
        }}
        placeholder="Name"
        css={{gridArea: 'key'}}
      />

      <TextInput
        label="Value"
        value={String(value ?? '')}
        onChange={v => {
          updateValue(v)
        }}
        style={{borderRadius: '5px', gridArea: 'value'}}
        error={submissionAttempted && formatMessage?.variant === 'warning'}
      />
      {formatMessage ? (
        <FieldInfo>
          <Info
            variant={submissionAttempted ? formatMessage.variant : 'info'}
            message={formatMessage.message}
          />
        </FieldInfo>
      ) : null}
      <Remove
        style={{
          gridArea: 'remove',
          display: 'flex',
          alignItems: 'center',
          visibility: isDisabled ? 'hidden' : 'visible'
        }}
      >
        <IconButton icon="clear" variant="greyscale" onClick={deleteProperty} />
      </Remove>
    </StyledInputGroup>
  )
}

const IntroWrap = styled.div``

const Intro = () => (
  <IntroWrap>
    <h2>Trigger an Atomic Action Flow</h2>
    <p>
      The Action Flow you trigger may send in-app action cards, push
      notifications, or run other flow steps designed in your Action Flow's
      configuration. After selecting an Action Flow provide values that are
      either static, or dynamically populated when this journey step is
      triggered, using{' '}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://developer.salesforce.com/docs/marketing/marketing-cloud/guide/how-data-binding-works.html"
      >
        SFMC Data Bindings
      </a>
      .
    </p>
  </IntroWrap>
)

/** place content in the centre of the screen */
const Centred = styled.div`
  width: 100%;
  justify-content: space-around;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const ConfigWrap = styled.div`
  // 960px is the width of the SFMC journey builder modal
  // this could be removed except it's nicer for local dev.
  max-width: 960px;
`

const InfoContainer = styled.div<{variant: 'warning' | 'info'}>`
  display: flex;
  gap: 4px;
  align-items: center;
  color: ${({variant, theme}) =>
    variant === 'warning' ? theme.colors.red10 : theme.colors.grey11};
  font-size: ${({theme}) => theme.fontSizeXS};
  & > svg {
    flex-shrink: 0;
  }
`
const GreyIcon = styled(InformationSmall)`
  color: ${({theme}) => theme.colors.grey11};
`
const WarningIcon = styled(WarningSmall)`
  color: ${({theme}) => theme.colors.red10};
`
const Info = ({
  variant,
  message
}: {
  variant: 'warning' | 'info'
  message: string
}) => (
  <InfoContainer variant={variant}>
    {variant === 'warning' ? <WarningIcon /> : <GreyIcon name="info" />}

    {message}
  </InfoContainer>
)

const getMappingValidation = (
  mappedValue?: string,
  atomicVariable?: Variable
) => {
  if (!atomicVariable)
    return {
      variant: 'info',
      message: 'This variable is not currently used by the selected Action Flow'
    } as const

  const disabled = !!get(atomicVariable, 'eventOverride.disabled')
  if (disabled)
    return {
      variant: 'warning' as const,
      message: 'Overriding this variable is disabled'
    }

  const required = !!get(atomicVariable, 'eventOverride.required')
  const typeMessage = makeTypeMessage(atomicVariable)

  if (!typeMessage && !required) return undefined
  return {
    variant:
      (required && !mappedValue) || disabled
        ? ('warning' as const)
        : ('info' as const),
    message: `${required ? 'Required variable' : ''} ${
      typeMessage ?? ''
    }`.trim()
  }
}

const makeTypeMessage = (variable: Variable) => {
  if (!variable.type || variable.type === 'string') return undefined
  return sentenceCase(
    `${variable.type} must match the format specified by Atomic`
  )
}

const sentenceCase = ([firstChar, ...rest]: string) =>
  (firstChar ?? '').toUpperCase() + rest.join('')

const getVariables = (actionFlow?: ActionFlowOption) => {
  return Object.fromEntries(
    Object.entries(actionFlow?.currentConfig?.variables ?? {}).filter(
      // SFMC won't send a list
      ([, def]) => supportsRequestOverride(def)
    )
  )
}
